import { useMemo, useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2Icon, PlusIcon, SearchIcon, Trash2Icon } from 'lucide-react'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  createDiscount,
  deleteDiscount,
  getDiscountMemberAssignments,
  getDiscounts,
  manageMemberDiscount,
  updateDiscount,
} from '@/lib/api'
import { DEFAULT_TABLE_PAGE_SIZE, TABLE_PAGE_SIZE_OPTIONS } from '@/lib/constants'
import { formatDateTime } from '@/lib/date'
import { getErrorMessage } from '@/lib/error-messages'
import { queryKeys } from '@/lib/query-keys'
import type { Discount, DiscountMemberAssignment, DiscountType } from '@/types/domain'

type DialogMode = 'create' | 'edit'

function formatEuro(cents: number): string {
  return `${(cents / 100).toFixed(2).replace('.', ',')} €`
}

function formatDiscountValue(discount: Discount): string {
  if (discount.discount_type === 'percent') {
    return `${Number(discount.percent_off ?? 0).toString().replace('.', ',')} %`
  }

  return formatEuro(discount.amount_cents ?? 0)
}

function parseAmountCents(value: string): number | null {
  const parsed = Number(value.replace(',', '.'))
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return null
  }

  return Math.round(parsed * 100)
}

function parsePercent(value: string): number | null {
  const parsed = Number(value.replace(',', '.'))
  if (!Number.isFinite(parsed) || parsed <= 0 || parsed > 100) {
    return null
  }

  return Math.round(parsed * 100) / 100
}

export function AdminDiscountsPage() {
  const queryClient = useQueryClient()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [dialogMode, setDialogMode] = useState<DialogMode>('create')
  const [editingDiscount, setEditingDiscount] = useState<Discount | null>(null)
  const [title, setTitle] = useState('')
  const [discountType, setDiscountType] = useState<DiscountType>('percent')
  const [value, setValue] = useState('10')
  const [status, setStatus] = useState('active')
  const [searchTerm, setSearchTerm] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_TABLE_PAGE_SIZE)
  const [membersDiscount, setMembersDiscount] = useState<Discount | null>(null)
  const [assignmentToRemove, setAssignmentToRemove] = useState<DiscountMemberAssignment | null>(null)
  const [memberSearchTerm, setMemberSearchTerm] = useState('')
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [discountToDelete, setDiscountToDelete] = useState<Discount | null>(null)

  const discountsQuery = useQuery({
    queryKey: queryKeys.discounts,
    queryFn: getDiscounts,
  })

  const discountMembersQuery = useQuery({
    queryKey: membersDiscount
      ? queryKeys.discountMemberAssignments(membersDiscount.id)
      : ['admin', 'discounts', 'members', 'idle'],
    queryFn: () => getDiscountMemberAssignments(membersDiscount!.id),
    enabled: Boolean(membersDiscount),
  })

  const createMutation = useMutation({
    mutationFn: createDiscount,
    onSuccess: (discount) => {
      closeDialog()
      queryClient.invalidateQueries({ queryKey: queryKeys.discounts })
      toast.success(`Rabatt "${discount.title}" wurde erstellt.`)
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const updateMutation = useMutation({
    mutationFn: updateDiscount,
    onSuccess: (discount) => {
      closeDialog()
      queryClient.invalidateQueries({ queryKey: queryKeys.discounts })
      toast.success(`Rabatt "${discount.title}" wurde aktualisiert.`)
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const removeAssignmentMutation = useMutation({
    mutationFn: (assignment: DiscountMemberAssignment) =>
      manageMemberDiscount({
        userId: assignment.user_id,
        discountId: assignment.discount_id,
        action: 'remove',
    }),
    onSuccess: (_, assignment) => {
      setAssignmentToRemove(null)
      queryClient.invalidateQueries({
        queryKey: queryKeys.discountMemberAssignments(assignment.discount_id),
      })
      queryClient.invalidateQueries({ queryKey: queryKeys.discounts })
      queryClient.invalidateQueries({
        queryKey: queryKeys.memberDiscountAssignments(assignment.user_id),
      })
      toast.success('Rabatt wurde vom Mitglied entfernt.')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const deleteMutation = useMutation({
    mutationFn: deleteDiscount,
    onSuccess: () => {
      setDeleteConfirmOpen(false)
      setDiscountToDelete(null)
      queryClient.invalidateQueries({ queryKey: queryKeys.discounts })
      toast.success('Rabatt wurde gelöscht.')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  function resetDialog() {
    setEditingDiscount(null)
    setTitle('')
    setDiscountType('percent')
    setValue('10')
    setStatus('active')
  }

  function closeDialog() {
    setDialogOpen(false)
    resetDialog()
  }

  function openCreateDialog() {
    resetDialog()
    setDialogMode('create')
    setDialogOpen(true)
  }

  function openEditDialog(discount: Discount) {
    setEditingDiscount(discount)
    setDialogMode('edit')
    setTitle(discount.title)
    setDiscountType(discount.discount_type)
    setValue(
      discount.discount_type === 'percent'
        ? String(discount.percent_off ?? '')
        : String(((discount.amount_cents ?? 0) / 100).toFixed(2)),
    )
    setStatus(discount.is_active ? 'active' : 'inactive')
    setDialogOpen(true)
  }

  function openMembersDialog(discount: Discount) {
    setMembersDiscount(discount)
  }

  function closeMembersDialog() {
    setMembersDiscount(null)
    setAssignmentToRemove(null)
    setMemberSearchTerm('')
  }

  function openRemoveConfirm(assignment: DiscountMemberAssignment) {
    setAssignmentToRemove(assignment)
  }

  function openDeleteConfirm(discount: Discount) {
    setDiscountToDelete(discount)
    setDeleteConfirmOpen(true)
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()

    const normalizedTitle = title.trim()
    if (!normalizedTitle) {
      toast.error(getErrorMessage(new Error('DISCOUNT_TITLE_REQUIRED')))
      return
    }

    const percentOff = discountType === 'percent' ? parsePercent(value) : null
    const amountCents = discountType === 'amount' ? parseAmountCents(value) : null

    if (discountType === 'percent' && percentOff === null) {
      toast.error(getErrorMessage(new Error('DISCOUNT_PERCENT_INVALID')))
      return
    }

    if (discountType === 'amount' && amountCents === null) {
      toast.error(getErrorMessage(new Error('DISCOUNT_AMOUNT_INVALID')))
      return
    }

    if (dialogMode === 'edit' && editingDiscount) {
      updateMutation.mutate({
        id: editingDiscount.id,
        title: normalizedTitle,
        discountType,
        percentOff,
        amountCents,
        isActive: status === 'active',
      })
      return
    }

    createMutation.mutate({
      title: normalizedTitle,
      discountType,
      percentOff,
      amountCents,
    })
  }

  const discounts = useMemo(() => discountsQuery.data ?? [], [discountsQuery.data])
  const filteredDiscounts = useMemo(() => {
    const normalized = searchTerm.trim().toLowerCase()
    if (!normalized) {
      return discounts
    }

    return discounts.filter((discount) =>
      [
        discount.title,
        discount.discount_type === 'percent' ? 'prozent' : 'betrag',
        formatDiscountValue(discount),
        discount.is_active ? 'aktiv' : 'inaktiv',
      ].some((value) => value.toLowerCase().includes(normalized)),
    )
  }, [discounts, searchTerm])

  const totalDiscounts = filteredDiscounts.length
  const totalPages = Math.max(1, Math.ceil(totalDiscounts / pageSize))
  const currentPage = Math.min(page, totalPages)
  const paginatedDiscounts = filteredDiscounts.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize,
  )
  const saving = createMutation.isPending || updateMutation.isPending
  const assignedMembers = useMemo(
    () => discountMembersQuery.data ?? [],
    [discountMembersQuery.data],
  )
  const filteredAssignedMembers = useMemo(() => {
    const normalized = memberSearchTerm.trim().toLowerCase()
    if (!normalized) {
      return assignedMembers
    }

    return assignedMembers.filter((assignment) => {
      const fullName = `${assignment.first_name} ${assignment.last_name}`.trim()
      return [
        assignment.first_name,
        assignment.last_name,
        fullName,
        assignment.email,
      ].some((value) => value.toLowerCase().includes(normalized))
    })
  }, [assignedMembers, memberSearchTerm])

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Rabatte</h1>
          <p className="text-sm text-muted-foreground">
            Verwalte personenbezogene Rabatte für Paketkäufe und laufende Abos.
          </p>
        </div>
        <Button type="button" onClick={openCreateDialog}>
          <PlusIcon className="mr-2 h-4 w-4" />
          Rabatt erstellen
        </Button>
      </div>

      <Dialog open={dialogOpen} onOpenChange={(open) => (open ? setDialogOpen(true) : closeDialog())}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {dialogMode === 'edit' ? 'Rabatt bearbeiten' : 'Rabatt erstellen'}
            </DialogTitle>
          </DialogHeader>
          <form className="space-y-5" onSubmit={handleSubmit}>
            <div className="space-y-2">
              <Label htmlFor="discount-title">Titel</Label>
              <Input
                id="discount-title"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                placeholder="z. B. Rabatt FitInn"
                disabled={saving}
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Art</Label>
                <Select
                  value={discountType}
                  onValueChange={(nextValue) => setDiscountType(nextValue as DiscountType)}
                  disabled={saving}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percent">Prozent</SelectItem>
                    <SelectItem value="amount">Eurobetrag</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="discount-value">
                  {discountType === 'percent' ? 'Prozentwert' : 'Eurobetrag'}
                </Label>
                <Input
                  id="discount-value"
                  type="number"
                  min={discountType === 'percent' ? 0.01 : 0.01}
                  max={discountType === 'percent' ? 100 : undefined}
                  step={discountType === 'percent' ? 0.01 : 0.01}
                  value={value}
                  onChange={(event) => setValue(event.target.value)}
                  disabled={saving}
                />
              </div>
            </div>
            {dialogMode === 'edit' && (
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={status} onValueChange={setStatus} disabled={saving}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Aktiv</SelectItem>
                    <SelectItem value="inactive">Inaktiv</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeDialog} disabled={saving}>
                Abbrechen
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? 'Speichert...' : 'Speichern'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={deleteConfirmOpen}
        onOpenChange={(open) => {
          setDeleteConfirmOpen(open)
          if (!open && !deleteMutation.isPending) {
            setDiscountToDelete(null)
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rabatt löschen?</DialogTitle>
            <DialogDescription>
              Dieser Rabatt wird deaktiviert und für künftige Käufe sowie laufende Abos entfernt.
              Bestehende Zahlungsprotokolle bleiben erhalten.
            </DialogDescription>
          </DialogHeader>

          {discountToDelete ? (
            <div className="rounded-md border p-3 text-sm">
              <span className="text-muted-foreground">Rabatt: </span>
              <span className="font-medium">{discountToDelete.title}</span>
            </div>
          ) : null}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setDeleteConfirmOpen(false)
                setDiscountToDelete(null)
              }}
              disabled={deleteMutation.isPending}
            >
              Abbrechen
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => {
                if (discountToDelete) {
                  deleteMutation.mutate(discountToDelete.id)
                }
              }}
              disabled={deleteMutation.isPending || !discountToDelete}
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />
                  Löscht ...
                </>
              ) : (
                'Löschen'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(membersDiscount)}
        onOpenChange={(open) => {
          if (!open && !removeAssignmentMutation.isPending) {
            closeMembersDialog()
          }
        }}
      >
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Mitglieder</DialogTitle>
            <DialogDescription>
              {membersDiscount
                ? `Aktive Zuweisungen für "${membersDiscount.title}".`
                : 'Aktive Zuweisungen für diesen Rabatt.'}
            </DialogDescription>
          </DialogHeader>

          <div className="relative">
            <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={memberSearchTerm}
              onChange={(event) => setMemberSearchTerm(event.target.value)}
              placeholder="Mitglieder suchen"
              className="pl-9"
            />
          </div>

          {discountMembersQuery.isLoading ? (
            <Skeleton className="h-40 w-full" />
          ) : assignedMembers.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Diesem Rabatt sind aktuell keine Mitglieder zugewiesen.
            </p>
          ) : filteredAssignedMembers.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Keine Mitglieder für diese Suche gefunden.
            </p>
          ) : (
            <div className="max-h-[60vh] overflow-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>E-Mail</TableHead>
                    <TableHead>Zugewiesen</TableHead>
                    <TableHead className="w-48 text-right">Aktion</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAssignedMembers.map((assignment) => {
                    const fullName = `${assignment.first_name} ${assignment.last_name}`.trim()
                    const confirmingRemove =
                      assignmentToRemove?.assignment_id === assignment.assignment_id
                    return (
                      <TableRow
                        key={assignment.assignment_id}
                        className={confirmingRemove ? 'bg-muted/40' : undefined}
                      >
                        <TableCell className="font-medium">
                          {fullName || 'Ohne Namen'}
                        </TableCell>
                        <TableCell>{assignment.email}</TableCell>
                        <TableCell>{formatDateTime(assignment.assigned_at)}</TableCell>
                        <TableCell className="text-right">
                          {confirmingRemove ? (
                            <div className="flex justify-end gap-2">
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => setAssignmentToRemove(null)}
                                disabled={removeAssignmentMutation.isPending}
                              >
                                Abbrechen
                              </Button>
                              <Button
                                type="button"
                                variant="destructive"
                                size="sm"
                                onClick={() => removeAssignmentMutation.mutate(assignment)}
                                disabled={removeAssignmentMutation.isPending}
                              >
                                {removeAssignmentMutation.isPending ? (
                                  <>
                                    <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />
                                    Entfernt ...
                                  </>
                                ) : (
                                  'Entfernen'
                                )}
                              </Button>
                            </div>
                          ) : (
                            <Button
                              type="button"
                              variant="destructive"
                              size="icon"
                              onClick={() => openRemoveConfirm(assignment)}
                              disabled={removeAssignmentMutation.isPending}
                              aria-label="Rabatt entfernen"
                            >
                              <Trash2Icon className="h-4 w-4" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={closeMembersDialog}
              disabled={removeAssignmentMutation.isPending}
            >
              Schließen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card>
        <CardHeader className="gap-3">
          <CardTitle className="text-lg">Rabattliste</CardTitle>
          <div className="grid gap-3 md:grid-cols-[1fr_9rem]">
            <div className="relative">
              <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={searchTerm}
                onChange={(event) => {
                  setSearchTerm(event.target.value)
                  setPage(1)
                }}
                placeholder="Rabatte suchen"
                className="pl-9"
              />
            </div>
            <Select
              value={String(pageSize)}
              onValueChange={(nextValue) => {
                setPageSize(Number(nextValue))
                setPage(1)
              }}
            >
              <SelectTrigger className="bg-card">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TABLE_PAGE_SIZE_OPTIONS.map((size) => (
                  <SelectItem key={size} value={String(size)}>
                    {size} pro Seite
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {discountsQuery.isLoading ? (
            <Skeleton className="h-40 w-full" />
          ) : paginatedDiscounts.length === 0 ? (
            <p className="text-sm text-muted-foreground">Keine Rabatte gefunden.</p>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Titel</TableHead>
                    <TableHead>Wert</TableHead>
                    <TableHead>Zuweisungen</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Erstellt</TableHead>
                    <TableHead className="text-right">Aktionen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedDiscounts.map((discount) => (
                    <TableRow key={discount.id}>
                      <TableCell className="font-medium">{discount.title}</TableCell>
                      <TableCell>{formatDiscountValue(discount)}</TableCell>
                      <TableCell>{discount.active_assigned_count}</TableCell>
                      <TableCell>
                        <Badge variant={discount.is_active ? 'default' : 'secondary'}>
                          {discount.is_active ? 'Aktiv' : 'Inaktiv'}
                        </Badge>
                      </TableCell>
                      <TableCell>{formatDateTime(discount.created_at)}</TableCell>
                      <TableCell>
                        <div className="flex justify-end gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => openMembersDialog(discount)}
                          >
                            Mitglieder
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => openEditDialog(discount)}
                          >
                            Bearbeiten
                          </Button>
                          <Button
                            type="button"
                            variant="destructive"
                            size="icon"
                            onClick={() => openDeleteConfirm(discount)}
                            disabled={deleteMutation.isPending}
                            aria-label="Rabatt löschen"
                          >
                            <Trash2Icon className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="flex items-center justify-between gap-3 text-sm text-muted-foreground">
                <p>
                  {(currentPage - 1) * pageSize + 1} -{' '}
                  {Math.min((currentPage - 1) * pageSize + paginatedDiscounts.length, totalDiscounts)}{' '}
                  von {totalDiscounts}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage <= 1}
                  >
                    Zurück
                  </Button>
                  <span>
                    Seite {currentPage} / {totalPages}
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage >= totalPages}
                  >
                    Weiter
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
